import { useMutation, useSuspenseQuery } from '@apollo/client';
import React, { useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { InlineEdit, PageTitle, VStack, useToast } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { TabNav } from '@eluve/smart-blocks';

import { promptTemplateUsersFragment } from './PromptTemplateUsers';
import {
  promptTemplateModelsFragment,
  promptTemplateVariantsFragment,
  updatePromptTemplateMutation,
} from './prompt.operations';

export interface PromptTemplateDetailsLayoutProps {}

const getPromptDetailsQuery = graphql(
  `
    query getPromptDetails($id: uuid!) {
      promptTemplatesByPk(id: $id) {
        __typename
        id
        name
        description
        isActive
        outputType
        ...PromptTemplateModels
        ...PromptTemplateUsers
        ...PromptTemplateVariants
      }
    }
  `,
  [
    promptTemplateUsersFragment,
    promptTemplateModelsFragment,
    promptTemplateVariantsFragment,
  ],
);

export const PromptTemplateDetailsLayout: React.FC<
  PromptTemplateDetailsLayoutProps
> = () => {
  const toast = useToast();
  const { promptTemplateId } = useParams() as { promptTemplateId: string };
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    data: { promptTemplatesByPk: promptTemplate },
  } = useSuspenseQuery(getPromptDetailsQuery, {
    variables: {
      id: promptTemplateId,
    },
  });

  const [updatePromptTemplate] = useMutation(updatePromptTemplateMutation, {
    onError: () => {
      toast.error('Failed to update template');
      setIsUpdating(false);
    },
    onCompleted: () => {
      setIsUpdating(false);
    },
  });

  const handleUpdateName = async (newName: string) => {
    setIsUpdating(true);
    await updatePromptTemplate({
      variables: {
        id: promptTemplateId,
        name: newName,
        description: promptTemplate?.description,
      },
    });
  };

  const handleUpdateDescription = async (newDescription: string) => {
    setIsUpdating(true);
    await updatePromptTemplate({
      variables: {
        id: promptTemplateId,
        name: promptTemplate?.name,
        description: newDescription,
      },
    });
  };

  return (
    <div>
      <VStack gap={2}>
        <InlineEdit
          value={promptTemplate?.name ?? ''}
          onSave={handleUpdateName}
          isLoading={isUpdating}
          placeholder="Template name"
          className="w-full"
        >
          <PageTitle>{promptTemplate?.name}</PageTitle>
        </InlineEdit>

        <InlineEdit
          value={promptTemplate?.description ?? ''}
          onSave={handleUpdateDescription}
          isLoading={isUpdating}
          multiline
          placeholder="Template description"
          className="w-full text-sm"
        >
          <span className="text-sm">{promptTemplate?.description}</span>
        </InlineEdit>
      </VStack>

      <VStack className="my-2">
        <p>
          Output Type:
          <span className="font-semibold"> {promptTemplate?.outputType}</span>
        </p>
        <p>
          Is Active:
          <span className="font-semibold">
            {' '}
            {promptTemplate?.isActive?.toString().toUpperCase()}
          </span>
        </p>
      </VStack>
      <TabNav
        tabs={[
          {
            name: 'Prompt',
            link: 'prompt',
          },
          {
            name: 'Model Args',
            link: 'model-args',
          },
          {
            name: 'Users',
            link: 'users',
          },
        ]}
      />
      <div className="mt-4">
        <Outlet />
      </div>
    </div>
  );
};
